import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  List,
  Stack,
  useToast,
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Loading } from '@/components/Loading'

import { OrganizationTreeSelect, OrganizationUnitTreeFlatten, type OrganizationUnitTree } from 'BrokerTsLib/src/containers'
import _ from 'lodash'
import { Section } from '../../components/Section'
import { PageContent } from '../../containers/PageContent'
import { PageToolbar } from '../../containers/PageToolbar'
import { SectionHeading } from '../../containers/shared/SectionHeading'
import {
  useGetAvailableOus,
  useGetInsuranceCategoryInsurers,
  useGetRtpcDetail,
  useSaveRtpcOus,
} from '../../hooks/useRtpc'
import { ArrowLeft } from '../../icons'

export const SetRtpcOrganizationsPage = () => {
  const { rtpcId: rtpcIdStr } = useParams()
  const rtpcId = +rtpcIdStr!
  const navigate = useNavigate()
  const toast = useToast()

  const { data: availableOusData, isLoading: getAvailableOusLoading } =
    useGetAvailableOus(rtpcId)
  const { data: rtpcData, isLoading: getRtpcDetailLoading } =
    useGetRtpcDetail(rtpcId)
  const { mutateAsync: saveRtpcOus, isLoading } = useSaveRtpcOus(rtpcId)
  const { getInsuranceCategoryInsurerText } = useGetInsuranceCategoryInsurers()

  const flatData = useMemo(
    () => availableOusData?.data.flatMap((x) => OrganizationUnitTreeFlatten(x)),
    [availableOusData?.data],
  )

  const [selectedOrganizations, setSelectedOrganizations] = useState<
    OrganizationUnitTree[] | undefined
  >()

  const setSelectedOrganizationsWithIds = useCallback(
    (ids: number[] | undefined) => {
      if (flatData && ids)
        setSelectedOrganizations(
          _.uniqBy(
            flatData
              .filter((x) => ids.includes(x.id))
              .flatMap((x) => [x, ...x.children]),
            (x) => x.id,
          ) || [],
        )
    },
    [flatData],
  )

  useEffect(() => {
    if (flatData)
      setSelectedOrganizationsWithIds(
        rtpcData?.data.ouIds.filter((ouId) =>
          flatData.some((availableOu) => availableOu.id === ouId),
        ),
      )
  }, [flatData, rtpcData, setSelectedOrganizationsWithIds])

  const save = () => {
    if (selectedOrganizations)
      saveRtpcOus({ rtpcId, ouIds: selectedOrganizations.map((x) => x.id) })
        .then(() => {
          toast({
            title: 'Uloženo',
            status: 'success',
          })
        })
        .catch(() => {
          toast({
            title: 'Chyba při ukládání',
            status: 'error',
          })
        })
  }

  return (
    <Section>
      <Container>
        <PageContent>
          <PageToolbar
            leftElements={
              <Stack>
                <Heading as="h1" fontSize="lg">
                  Nastavení RTPC organizacím
                </Heading>
                {rtpcData?.data && (
                  <Heading as="h2" fontWeight="thin">
                    {' '}
                    {getInsuranceCategoryInsurerText(
                      rtpcData?.data.rtpc.insuranceCategoryInsurerId,
                    )}
                    {' - '}
                    {rtpcData?.data.rtpc.coef * 100} %
                  </Heading>
                )}
              </Stack>
            }
            rightElements={
              <Button variant="ghost" onClick={() => navigate('/rtpc')}>
                <ArrowLeft color="secondary.500" />
                Zpět na správu RTPC
              </Button>
            }
          />

          {getAvailableOusLoading ||
            getRtpcDetailLoading ||
            !selectedOrganizations ? (
            <Loading></Loading>
          ) : (
            <>
              <Flex gap={8}>
                <Flex flexDir={'column'} gap={4} minW={'sm'}>
                  <SectionHeading>Výběr organizací</SectionHeading>
                  {availableOusData?.data &&
                    rtpcData?.data.ouIds instanceof Array && (
                      <OrganizationTreeSelect
                        data={availableOusData?.data}
                        defaultSelectedIds={selectedOrganizations.map(
                          (x) => x.id,
                        )}
                        onSelectedIdsChanged={setSelectedOrganizationsWithIds}
                      />
                    )}
                </Flex>
                <Flex flexDir={'column'} gap={4} minW={'sm'}>
                  <SectionHeading>Vybrané organizace</SectionHeading>
                  <List>
                    {selectedOrganizations.map((ou) => (
                      <Box key={ou.id}>{ou.name}</Box>
                    ))}
                  </List>
                </Flex>
              </Flex>
              <Box>
                <Button
                  colorScheme="primary"
                  px={8}
                  onClick={save}
                  isLoading={isLoading}
                >
                  Uložit
                </Button>
              </Box>
            </>
          )}
        </PageContent>
      </Container>
    </Section>
  )
}
